import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useStore } from "react-redux"
import { useNavigate } from "react-router"
import { addToCart, updateQuantity, type CartItem, type ICart } from "../../../store/Cart"
import { message } from "../../../utils/message"
import { renderBusinessName } from "../../../utils/render-business-name"
import type { CatalogueProduct } from "../product-list/catalogue-data"
import { getProductPrice } from "../product-list/utils"

export type CartLineSource = {
  variantId: string
  parentVariantId?: string | null
  quantity: number
  price?: number | null
  name?: string | null
  skuCode?: string | null
  image?: string | null
  /** Used when the variant is not in the catalogue (a past order line keeps its warehouse). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inventories?: any[]
}

/** One line to add, with the most the cart may hold of it (the stock), when known. */
export type CartAddEntry = { item: CartItem; max?: number }

/**
 * A cart line in the shape the catalogue puts it there (product-list/index.tsx
 * addToCart): the catalogue product spread first, so warehouse, VAT and minimum
 * order travel with it; a variant missing from the catalogue gets the bare fields.
 */
export function toCartItem(
  line: CartLineSource,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supplier: any,
  product?: CatalogueProduct | null,
): CartItem {
  const inventories = Array.isArray(product?.inventories) && product!.inventories.length > 0 ? product!.inventories : line.inventories ?? []
  return {
    ...(product || {}),
    id: line.variantId,
    variantId: line.variantId,
    variantParentId: line.parentVariantId || line.variantId,
    supplierBusinessId: supplier?.id,
    supplierName: renderBusinessName(supplier, ""),
    price: Number(line.price) > 0 ? Number(line.price) : product ? getProductPrice(product) : 0,
    quantity: line.quantity,
    nameMon: product?.nameMon ?? line.name,
    skuCode: product?.skuCode ?? line.skuCode,
    image: product?.image ?? line.image,
    inventories,
  }
}

/** Order create / draft require a warehouse per line (cart-model lineFor reads inventories[0]). */
export const hasWarehouse = (item: CartItem) => !!item.inventories?.[0]?.warehouseId

/**
 * Adds lines to the cart and opens the catalogue. A line already in the cart gets
 * the quantity added, never past `max`; a line without a warehouse is not added.
 * `skipped` lists SKUs the caller already left out for the same reason.
 */
export function useAddLinesToCart() {
  const { t } = useTranslation("bulkOrder")
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const store = useStore<{ cart: ICart }>()

  return useCallback(
    (entries: CartAddEntry[], skipped: string[] = []) => {
      const skippedText = (list: string[]) => {
        const skus = list.filter(Boolean).join(", ")
        return skus ? t("skippedNote", { count: list.length, skus }) : t("skippedNoteCount", { count: list.length })
      }
      const noWarehouse = [...skipped]
      let added = 0
      let capped = 0
      entries.forEach(({ item, max }) => {
        if (!hasWarehouse(item)) {
          noWarehouse.push(String(item.skuCode || item.nameMon || item.variantId))
          return
        }
        const limit = max != null && max >= 0 ? max : Infinity
        const existing = store
          .getState()
          .cart.items.find((i) => i.id === item.id && i.supplierBusinessId === item.supplierBusinessId)
        const current = existing?.quantity || 0
        const next = Math.min(current + item.quantity, limit)
        if (next < current + item.quantity) capped += 1
        if (next <= current) return
        if (existing) {
          dispatch(updateQuantity({ productId: item.id, supplierBusinessId: item.supplierBusinessId, quantity: next }))
        } else {
          dispatch(addToCart({ ...item, quantity: next }))
        }
        added += 1
      })

      const notes = [
        capped > 0 ? t("cappedNote", { count: capped }) : "",
        noWarehouse.length > 0 ? skippedText(noWarehouse) : "",
      ].filter(Boolean)
      if (added === 0) {
        message.error([t("nothingAdded"), ...notes].join(". "))
        return
      }
      message.success([t("added", { count: added }), ...notes].join(". "))
      navigate("/buyer-sales/new")
    },
    [store, dispatch, navigate, t],
  )
}
